import { useEffect, useRef } from 'react';
import type { ReactNode } from 'react';

interface MagneticProps {
  children: ReactNode;
  /** How strongly the contents follow the cursor. 0 = none, 1 = full. */
  strength?: number;
}

/**
 * Wraps its children in a span that gently translates toward the cursor on
 * hover. Disabled on touch-only devices.
 */
export function Magnetic({ children, strength = 0.25 }: MagneticProps) {
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    if (!window.matchMedia?.('(hover: hover)').matches) return;

    const onMove = (event: MouseEvent) => {
      const rect = el.getBoundingClientRect();
      const dx = event.clientX - rect.left - rect.width / 2;
      const dy = event.clientY - rect.top - rect.height / 2;
      el.style.transform = `translate(${dx * strength}px, ${dy * strength}px)`;
    };

    const onLeave = () => {
      el.style.transform = '';
    };

    el.addEventListener('mousemove', onMove);
    el.addEventListener('mouseleave', onLeave);
    return () => {
      el.removeEventListener('mousemove', onMove);
      el.removeEventListener('mouseleave', onLeave);
    };
  }, [strength]);

  return (
    <div ref={ref} className="magnetic">
      {children}
    </div>
  );
}
